import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth.jsx";
import { LOGO_COLOR } from "@/lib/brand";
import { Loader2, Mail, Lock } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(form.email, form.password);
      toast.success(`Welcome back, ${user.name.split(" ")[0]}!`);
      navigate(user.role === "admin" ? "/admin" : "/app");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-mesh bg-brand-bg flex items-center justify-center p-5">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <img src={LOGO_COLOR} alt="DraftVoice" className="h-10 w-10 rounded-lg bg-brand-navy" />
          <span className="font-bold text-xl text-brand-navy">Draft<span className="text-gradient-primary">Voice</span> AI</span>
        </Link>
        <div className="bg-white rounded-3xl shadow-soft border border-brand-borderc p-8">
          <h1 className="text-2xl font-bold text-brand-navy">Welcome back</h1>
          <p className="text-sm text-brand-muted mt-1">Speak it. We draft it.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="text-sm font-medium text-brand-text">Email</label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-3 w-4 h-4 text-brand-muted" />
                <input
                  data-testid="login-email-input"
                  type="email" required
                  value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-brand-borderc bg-white focus:border-brand-purple focus:ring-2 focus:ring-brand-purple/20 outline-none"
                  placeholder="you@company.com"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-brand-text">Password</label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-3 w-4 h-4 text-brand-muted" />
                <input
                  data-testid="login-password-input"
                  type="password" required minLength={6}
                  value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-brand-borderc bg-white focus:border-brand-purple focus:ring-2 focus:ring-brand-purple/20 outline-none"
                  placeholder="••••••••"
                />
              </div>
            </div>
            <button
              data-testid="login-submit-btn"
              type="submit" disabled={loading}
              className="w-full btn-primary-gradient rounded-full py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Log in
            </button>
          </form>
          <div className="mt-5 text-center text-sm text-brand-muted">
            New here? <Link to="/signup" className="text-brand-blue font-semibold hover:underline">Create an account</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
